import { useMemo } from 'react';
import { useListAttachmentsQuery } from '@qapline/core';
import { ELanguage } from '@qapline/translations';
import { TMailModalFormSchema, TMailModalTransformedValues } from 'modules/ui/components/MailModal/MailModal.types';

import { useGetInvoiceQuery, useMailInvoiceMutation } from '../api';

export const useMailInvoice = (invoiceId: number) => {
    const { data: attachments, isFetching: isFetchingAttachments } = useListAttachmentsQuery({
        invoiceId,
        withCommissionsDeliveryNotes: true,
    });
    const { data: invoice, isFetching: isFetchingInvoice } = useGetInvoiceQuery({ invoiceId });
    const [mailInvoice, { isLoading: isSending }] = useMailInvoiceMutation();

    const mailAttachments = useMemo((): TMailModalFormSchema['attachments'] => {
        if (!attachments || attachments.length === 0) return [];
        return attachments.map(({ attachment_id, name }) => ({ value: attachment_id, label: name }));
    }, [attachments]);

    const mailRecipients = useMemo((): TMailModalFormSchema['recipients'] => {
        const billingEmail = invoice?.customer?.billingEmail;
        if (!billingEmail) return [];
        return [
            {
                value: billingEmail,
                label: billingEmail,
            },
        ];
    }, [invoice]);

    const languageCode: ELanguage | null = useMemo(() => {
        if (!invoice) return null;
        return invoice.language.languageCode as ELanguage;
    }, [invoice]);

    const handleMailInvoice = async ({ recipients, message }: TMailModalTransformedValues) => {
        try {
            if (!invoice || !languageCode) throw new Error('Missing data for invoice mailing');
            const { message: resMessage, rejectedEmails } = await mailInvoice({
                emails: recipients,
                message,
                invoiceId,
                lang: languageCode,
            }).unwrap();

            return {
                success: true,
                message: resMessage,
                rejectedEmails,
            };
        } catch (error: any) {
            return {
                success: false,
                message: error?.data?.message,
                error: error,
            };
        }
    };

    return {
        isSending,
        languageCode,
        handleMailInvoice,
        recipients: mailRecipients,
        attachments: mailAttachments,
        isFetching: isFetchingAttachments || isFetchingInvoice,
    };
};
